import { Primitive, z, ZodLiteral, ZodNever } from 'zod'

/*
 * Zod helper to create a union of literals from an array of primitive values.
 * Zod does not accept an array of literals directly, so we need to map them to ZodLiteral types.
 * If the array is empty, it returns ZodNever.
 * If the array has one item, it returns a single ZodLiteral.
 * https://github.com/colinhacks/zod/issues/831#issuecomment-997444465
 */

type MappedZodLiterals<T extends readonly Primitive[]> = {
  -readonly [K in keyof T]: ZodLiteral<T[K]>
}

function createManyUnion<
  A extends Readonly<[Primitive, Primitive, ...Primitive[]]>,
>(literals: A) {
  return z.union(
    literals.map((value) => z.literal(value)) as MappedZodLiterals<A>
  )
}

function createUnionSchema<T extends readonly []>(values: T): ZodNever
function createUnionSchema<T extends readonly [Primitive]>(
  values: T
): ZodLiteral<T[0]>
function createUnionSchema<
  T extends readonly [Primitive, Primitive, ...Primitive[]],
>(values: T): z.ZodUnion<MappedZodLiterals<T>>
function createUnionSchema<T extends readonly Primitive[]>(values: T) {
  if (values.length > 1) {
    return createManyUnion(
      values as typeof values & [Primitive, Primitive, ...Primitive[]]
    )
  } else if (values.length === 1) {
    return z.literal(values[0])
  } else if (values.length === 0) {
    return z.never()
  }
  throw new Error('Array must have a length')
}
export { createUnionSchema }
